import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  Select as ChakraSelect,
  useToast,
  Text,
  Icon,
  Badge,
  Box,
  Divider,
} from '@chakra-ui/react';
import { FiCalendar, FiClock } from 'react-icons/fi';
import moment from 'moment';
import { useAuth } from '../../../contexts/AuthContext';
import EntitySelector from '../EntitySelector';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';

/**
 * Unified Event Modal
 * 
 * Combines Task and Event creation into single interface.
 * Conditionally shows fields based on event type.
 * 
 * Event Types:
 * - Task: Saved to Supabase `crm_tasks` (timed tasks carry due_time + duration)
 * - Meeting: Saved to Supabase `crm_calendar_events` with event_type='meeting'
 * - General Event: Saved to Supabase `crm_calendar_events` with event_type='general'
 * 
 * Google Calendar sync is automatic: DB triggers enqueue every insert/update
 * and Cloud Functions push to Google within seconds.
 */

/**
 * Resolve a display name for the selected related entity regardless of how it
 * was produced:
 * - restored from an existing task in edit mode -> `entity.displayName`
 * - picked via EntitySelector / prefilled deal  -> full camelCase `entity.data`
 *
 * Contact entities restored from a task only carry `{ id }` in `data`, so
 * building the name from `data.firstName`/`data.lastName` here used to write
 * the literal string "undefined undefined" into related_entity_name on save.
 */
const getEntityDisplayName = (entity) => {
  if (!entity) return '';
  if (entity.displayName) return entity.displayName;
  const d = entity.data || {};
  if (entity.type === 'deal') return d.dealName || 'Deal';
  return `${d.firstName || ''} ${d.lastName || ''}`.trim() || 'Contact';
};

export default function UnifiedEventModal({ 
  isOpen, 
  onClose, 
  onSave,
  initialDate = null,
  prefilledEntity = null, // { type: 'deal'|'contact', data: {...} }
  initialEventType = 'meeting', // Default type: 'task' | 'meeting' | 'general'
  editingTask = null, // If provided, opens in edit mode with task data pre-filled
  crmUsers = [], // List of CRM users for task assignment (supports { uid, name } or { value, label } shapes)
}) {
  const { currentUser } = useAuth();
  const toast = useToast();
  const isEditMode = !!editingTask;

  const [eventType, setEventType] = useState(initialEventType);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium'); // For tasks
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [allDay, setAllDay] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState('30');
  const [attendees, setAttendees] = useState('');
  const [addVideoLink, setAddVideoLink] = useState(true);
  const [assignedTo, setAssignedTo] = useState(currentUser?.uid || '');
  const [isSaving, setIsSaving] = useState(false);
  const [showEntitySelector, setShowEntitySelector] = useState(false);
  // Tracks which task's related entity was already re-fetched, so the init
  // effect re-running (e.g. parent re-renders while open) doesn't refetch.
  const refreshedEntityTaskIdRef = useRef(null);

  // Re-fetch the restored entity's current name from Supabase. Self-heals
  // stale or corrupted related_entity_name values: the fresh name is shown in
  // the form and written back on the next save. On failure the restored
  // displayName is kept - editing must keep working offline of this.
  const refreshRestoredEntity = async (task) => {
    const { type, id } = task.relatedTo || {};
    if (!id || (type !== 'contact' && type !== 'deal') || !isSupabaseConfigured()) return;
    if (refreshedEntityTaskIdRef.current === task.id) return;
    refreshedEntityTaskIdRef.current = task.id;

    try {
      if (type === 'contact') {
        const { data, error } = await supabase
          .from('crm_contacts')
          .select('id, first_name, last_name, email')
          .eq('id', id)
          .maybeSingle();
        if (error || !data) return;
        const freshName = `${data.first_name || ''} ${data.last_name || ''}`.trim();
        setSelectedEntity(prev => {
          // The user may have picked a different entity while this was in flight
          if (!prev || prev.type !== 'contact' || prev.data?.id !== id) return prev;
          return {
            ...prev,
            displayName: freshName || prev.displayName,
            data: {
              ...prev.data,
              firstName: data.first_name,
              lastName: data.last_name,
              email: data.email,
            },
          };
        });
      } else {
        const { data, error } = await supabase
          .from('crm_deals')
          .select('id, deal_name')
          .eq('id', id)
          .maybeSingle();
        if (error || !data) return;
        setSelectedEntity(prev => {
          if (!prev || prev.type !== 'deal' || prev.data?.id !== id) return prev;
          return {
            ...prev,
            displayName: data.deal_name || prev.displayName,
            data: { ...prev.data, dealName: data.deal_name },
          };
        });
      }
    } catch (err) {
      console.warn('[UnifiedEventModal] Could not refresh related entity name:', err);
    }
  };

  // Initialize with defaults OR editing task data
  useEffect(() => {
    if (isOpen) {
      // EDIT MODE: Pre-fill from existing task
      if (editingTask) {
        setEventType('task'); // Tasks only for now
        setTitle(editingTask.title || '');
        setDescription(editingTask.description || '');
        setPriority(editingTask.priority || 'medium');
        setAssignedTo(editingTask.assignedTo || currentUser?.uid || '');
        
        // Set date/time from task dueDate
        if (editingTask.dueDate) {
          const dueDate = editingTask.dueDate.toDate ? editingTask.dueDate.toDate() : new Date(editingTask.dueDate);
          setStartDate(moment(dueDate).format('YYYY-MM-DD'));
          if (editingTask.dueTime) {
            setStartTime(editingTask.dueTime);
            setAllDay(false);
          } else {
            setAllDay(true);
          }
        }
        
        // Set duration if available
        if (editingTask.eventDuration) {
          setDuration(String(editingTask.eventDuration));
        }
        
        // Set related entity if available. Only the id is known here, so the
        // saved name is carried in displayName (never rebuilt from data) and
        // the live record is re-fetched to heal stale/corrupted names.
        if (editingTask.relatedTo) {
          setSelectedEntity({
            type: editingTask.relatedTo.type,
            displayName: editingTask.relatedTo.name,
            data: { id: editingTask.relatedTo.id },
          });
          refreshRestoredEntity(editingTask);
        } else if (prefilledEntity) {
          setSelectedEntity(prefilledEntity);
        }
      } 
      // CREATE MODE: Set defaults
      else {
        // Set event type to initial value when opening
        setEventType(initialEventType);
        setAssignedTo(currentUser?.uid || '');
        
        if (initialDate) {
          setStartDate(moment(initialDate).format('YYYY-MM-DD'));
          setStartTime(moment(initialDate).format('HH:mm'));
        } else {
          setStartDate(moment().format('YYYY-MM-DD'));
          setStartTime(moment().add(1, 'hour').startOf('hour').format('HH:mm'));
        }

        if (prefilledEntity) {
          setSelectedEntity(prefilledEntity);
          if (prefilledEntity.type === 'deal') {
            // Set title based on event type
            const prefix = initialEventType === 'task' ? 'Task' : 'Meeting';
            setTitle(`${prefix}: ${prefilledEntity.data.dealName || ''}`);
            if (prefilledEntity.contact?.email) {
              setAttendees(prefilledEntity.contact.email);
            }
          } else if (prefilledEntity.type === 'contact') {
            const prefix = initialEventType === 'task' ? 'Task' : 'Meeting';
            setTitle(`${prefix}: ${prefilledEntity.data.firstName} ${prefilledEntity.data.lastName}`);
            if (prefilledEntity.data.email) {
              setAttendees(prefilledEntity.data.email);
            }
          }
        }
      }
    }
  }, [isOpen, initialDate, prefilledEntity, initialEventType, editingTask]);

  const handleSave = async () => {
    // Validation
    if (!title.trim()) {
      toast({
        title: 'Title required',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    if (!startDate) {
      toast({
        title: 'Date required',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    if (!allDay && !startTime) {
      toast({
        title: 'Time required',
        description: 'Select a time or enable all-day',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    setIsSaving(true);
    try {
      let startDateTime, endDateTime;

      if (allDay) {
        startDateTime = moment(startDate).startOf('day').toDate();
        endDateTime = moment(startDate).endOf('day').toDate();
      } else {
        startDateTime = moment(`${startDate} ${startTime}`, 'YYYY-MM-DD HH:mm').toDate();
        endDateTime = moment(startDateTime).add(parseInt(duration), 'minutes').toDate();
      }

      // Prepare related entity data
      let relatedTo = null;
      if (selectedEntity) {
        relatedTo = {
          type: selectedEntity.type,
          id: selectedEntity.data.id,
          name: getEntityDisplayName(selectedEntity),
        };
      }

      if (eventType === 'task') {
        // Check if Supabase is configured before attempting database operations
        if (!isSupabaseConfigured()) {
          throw new Error('Database not configured. Please contact support.');
        }

        if (isEditMode) {
          // UPDATE existing task in Supabase
          const taskData = {
            title,
            description,
            priority,
            assigned_to: assignedTo || currentUser.uid,
            due_date: startDateTime.toISOString(),
            due_time: allDay ? null : startTime,
            event_duration_minutes: allDay ? null : parseInt(duration, 10),
            related_entity_type: relatedTo?.type || null,
            related_entity_id: relatedTo?.id || null,
            related_entity_name: relatedTo?.name || null,
            deal_id: relatedTo?.type === 'deal' ? relatedTo.id : null,
            contact_id: relatedTo?.type === 'contact' ? relatedTo.id : null,
          };

          const { error } = await supabase
            .from('crm_tasks')
            .update(taskData)
            .eq('id', editingTask.id);

          if (error) throw error;

          toast({
            title: 'Task updated',
            status: 'success',
            duration: 2000,
          });
        } else {
          // Validate user is logged in before creating task
          if (!currentUser?.uid) {
            throw new Error('You must be logged in to create tasks.');
          }

          // CREATE new task in Supabase
          const taskData = {
            title,
            description,
            type: 'crm',
            priority,
            status: 'pending',
            assigned_to: assignedTo || currentUser.uid,
            created_by: currentUser.uid,
            due_date: startDateTime.toISOString(),
            due_time: allDay ? null : startTime,
            event_duration_minutes: allDay ? null : parseInt(duration, 10),
            is_calendar_event: false,
            related_entity_type: relatedTo?.type || null,
            related_entity_id: relatedTo?.id || null,
            related_entity_name: relatedTo?.name || null,
            deal_id: relatedTo?.type === 'deal' ? relatedTo.id : null,
            contact_id: relatedTo?.type === 'contact' ? relatedTo.id : null,
          };

          console.log('[UnifiedEventModal] Creating task with:', { 
            assigned_to: taskData.assigned_to, 
            created_by: taskData.created_by,
            deal_id: taskData.deal_id 
          });

          const { data, error } = await supabase
            .from('crm_tasks')
            .insert(taskData)
            .select('*')
            .single();

          if (error) throw error;

          console.log('[UnifiedEventModal] Task created successfully:', data?.id);

          toast({
            title: 'Task created',
            status: 'success',
            duration: 2000,
          });
        }
      } else {
        // Save to crm_calendar_events (meeting or general event); the DB
        // trigger enqueues the Google push automatically
        if (!isSupabaseConfigured()) {
          throw new Error('Database not configured. Please contact support.');
        }
        if (!currentUser?.uid) {
          throw new Error('You must be logged in to create events.');
        }

        const attendeesList = attendees 
          ? attendees.split(',').map(e => e.trim()).filter(e => e) 
          : [];

        const eventData = {
          user_id: currentUser.uid,
          created_by: currentUser.uid,
          title,
          description,
          event_type: eventType, // 'meeting' or 'general'
          start_at: startDateTime.toISOString(),
          end_at: endDateTime.toISOString(),
          all_day: allDay,
          attendees: attendeesList,
          add_video_link: eventType === 'meeting' ? addVideoLink : false,
          related_entity_type: relatedTo?.type || null,
          related_entity_id: relatedTo?.id || null,
          related_entity_name: relatedTo?.name || null,
          deal_id: relatedTo?.type === 'deal' ? relatedTo.id : null,
          contact_id: relatedTo?.type === 'contact' ? relatedTo.id : null,
        };

        const { error } = await supabase.from('crm_calendar_events').insert(eventData);
        if (error) throw error;

        toast({
          title: `${eventType === 'meeting' ? 'Meeting' : 'Event'} created`,
          description: 'Syncing to Google Calendar',
          status: 'success',
          duration: 2000,
        });
      }

      // Call onSave callback if provided
      if (onSave) {
        await onSave();
      }

      // Reset form
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error creating event/task:', error);
      toast({
        title: 'Error creating event',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setEventType(initialEventType);
    setTitle('');
    setDescription('');
    setPriority('medium');
    setAssignedTo(currentUser?.uid || '');
    setSelectedEntity(null);
    setAllDay(false);
    setStartDate('');
    setStartTime('');
    setDuration('30');
    setAttendees('');
    setAddVideoLink(true);
    setShowEntitySelector(false);
  };

  const handleEntitySelected = (entity) => {
    setSelectedEntity(entity);
    setShowEntitySelector(false);
    
    // Auto-populate title if empty
    if (!title) {
      if (entity.type === 'deal') {
        setTitle(`Meeting: ${entity.data.dealName}`);
        if (entity.contact?.email) {
          setAttendees(entity.contact.email);
        }
      } else if (entity.type === 'contact') {
        setTitle(`Meeting: ${entity.data.firstName} ${entity.data.lastName}`);
        if (entity.data.email) {
          setAttendees(entity.data.email);
        }
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="auto">
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={FiCalendar} />
            <Text>{isEditMode ? 'Edit Task' : 'Add Event'}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Event Type Selector */}
            <FormControl isRequired>
              <FormLabel fontSize="sm">Event Type</FormLabel>
              <ChakraSelect
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
              >
                <option value="task">Task</option>
                <option value="meeting">Meeting</option>
                <option value="general">General Event</option>
              </ChakraSelect>
              <Text fontSize="xs" color="gray.600" mt={1}>
                {eventType === 'task' && 'Creates a to-do item in your task list'}
                {eventType === 'meeting' && 'Creates a calendar meeting with attendees and video link'}
                {eventType === 'general' && 'Creates a calendar event without meeting features'}
              </Text>
            </FormControl>

            <Divider />

            {/* Title */}
            <FormControl isRequired>
              <FormLabel fontSize="sm">Title</FormLabel>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={
                  eventType === 'task' 
                    ? 'Follow up with client...' 
                    : 'Meeting with client...'
                }
              />
            </FormControl>

            {/* Description */}
            <FormControl isRequired>
              <FormLabel fontSize="sm">Description</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Event details, agenda..."
                rows={3}
                size="sm"
              />
            </FormControl>

            {/* Task Priority (conditional) */}
            {eventType === 'task' && (
              <FormControl>
                <FormLabel fontSize="sm">Task Priority</FormLabel>
                <ChakraSelect
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  size="sm"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </ChakraSelect>
              </FormControl>
            )}

            {/* Assign To (for tasks, when CRM users are available) */}
            {eventType === 'task' && crmUsers.length > 0 && (
              <FormControl>
                <FormLabel fontSize="sm">Assign To</FormLabel>
                <ChakraSelect
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  size="sm"
                >
                  {crmUsers.map(user => (
                    <option key={user.uid || user.value} value={user.uid || user.value}>
                      {user.name || user.Name || user.label}
                    </option>
                  ))}
                </ChakraSelect>
              </FormControl>
            )}

            <Divider />

            {/* Contact/Loan Association (optional) */}
            <FormControl>
              <FormLabel fontSize="sm">Related Contact or Deal (Optional)</FormLabel>
              {showEntitySelector ? (
                <Box borderWidth="1px" borderRadius="md" p={3} borderColor="gray.200">
                  <EntitySelector
                    entityType="both"
                    onSelect={handleEntitySelected}
                    userId={currentUser?.uid}
                  />
                </Box>
              ) : selectedEntity ? (
                <Box p={3} bg="purple.50" borderRadius="md" borderWidth="1px" borderColor="purple.200">
                  <HStack justify="space-between">
                    <VStack align="flex-start" spacing={0}>
                      <Text fontSize="xs" fontWeight="semibold" color="purple.800">
                        {selectedEntity.type === 'deal' ? 'Linked to Deal' : 'Linked to Contact'}
                      </Text>
                      <Text fontSize="sm" color="purple.700">
                        {getEntityDisplayName(selectedEntity)}
                      </Text>
                    </VStack>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => setSelectedEntity(null)}
                    >
                      Remove
                    </Button>
                  </HStack>
                </Box>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowEntitySelector(true)}
                  w="full"
                >
                  Select Contact or Deal
                </Button>
              )}
            </FormControl>

            <Divider />

            {/* All Day Toggle */}
            <FormControl display="flex" alignItems="center">
              <FormLabel fontSize="sm" mb={0}>
                All Day Event/Task
              </FormLabel>
              <Switch
                isChecked={allDay}
                onChange={(e) => setAllDay(e.target.checked)}
                colorScheme="blue"
              />
            </FormControl>

            {/* Date */}
            <FormControl isRequired>
              <FormLabel fontSize="sm">Date</FormLabel>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                min={moment().format('YYYY-MM-DD')}
              />
            </FormControl>

            {/* Time (conditional) */}
            {!allDay && (
              <HStack>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Time</FormLabel>
                  <Input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Duration</FormLabel>
                  <ChakraSelect
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    size="sm"
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="90">1.5 hours</option>
                    <option value="120">2 hours</option>
                  </ChakraSelect>
                </FormControl>
              </HStack>
            )}

            {/* Attendees (for meetings only) */}
            {eventType === 'meeting' && (
              <>
                <Divider />
                <FormControl>
                  <FormLabel fontSize="sm">Attendees</FormLabel>
                  <Input
                    value={attendees}
                    onChange={(e) => setAttendees(e.target.value)}
                    placeholder="email1@example.com, email2@example.com"
                    size="sm"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Separate multiple emails with commas
                  </Text>
                </FormControl>

                {/* Video Link Toggle */}
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel fontSize="sm" mb={0}>
                    Add video meeting link
                  </FormLabel>
                  <Switch
                    isChecked={addVideoLink}
                    onChange={(e) => setAddVideoLink(e.target.checked)}
                    colorScheme="green"
                  />
                </FormControl>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText={isEditMode ? 'Updating...' : 'Creating...'}
          >
            {isEditMode 
              ? 'Update Task'
              : `Create ${eventType === 'task' ? 'Task' : eventType === 'meeting' ? 'Meeting' : 'Event'}`
            }
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

